import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { authService } from "../services/authService";
import { LoadingSpinner } from "./shared";
import Session from "supertokens-auth-react/recipe/session";
import { useUser, useAlbumShare, useAuth } from "../hooks";
import {
  buildBrowserShareUrl,
  buildMobileAppShareUrl,
  getMobilePlatform,
  getStoreUrl,
  isNativeAppShareHandoffEnabled,
} from "../utils/shareLinks";

interface MobileShareDecisionProps {
  onOpenApp: () => void;
  onContinueInBrowser: () => void;
  onGetApp: (() => void) | null;
}

const MobileShareDecision: React.FC<MobileShareDecisionProps> = ({
  onOpenApp,
  onContinueInBrowser,
  onGetApp,
}) => {
  return (
    <div
      data-section="shared-album-page-root"
      className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900"
    >
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-80 w-80 animate-blob rounded-full bg-purple-500 opacity-20 mix-blend-multiply blur-xl filter"></div>
        <div className="animation-delay-2000 absolute -bottom-40 -left-40 h-80 w-80 animate-blob rounded-full bg-blue-500 opacity-20 mix-blend-multiply blur-xl filter"></div>
        <div className="animation-delay-4000 absolute top-40 left-1/2 h-80 w-80 animate-blob rounded-full bg-pink-500 opacity-20 mix-blend-multiply blur-xl filter"></div>
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10">
        <div className="w-full max-w-md rounded-3xl border border-white/20 bg-white/10 p-8 text-center shadow-2xl backdrop-blur-xl">
          <div className="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-blue-500">
            <svg className="h-10 w-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.414-1.414m-.758-4.242a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.414 1.414"
              />
            </svg>
          </div>

          <h1 className="mb-3 text-3xl font-bold text-white">Open in joint.photos</h1>
          <p className="mb-8 text-gray-300">
            Open this shared album in the mobile app, continue in the browser, or install the app first.
          </p>

          <div className="space-y-3">
            <button
              onClick={onOpenApp}
              className="w-full rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-3 font-medium text-white transition-all duration-200 hover:-translate-y-0.5 hover:from-purple-500 hover:to-blue-500"
            >
              Open in App
            </button>

            <button
              onClick={onContinueInBrowser}
              className="w-full rounded-xl border border-white/20 bg-white/10 px-6 py-3 font-medium text-white transition-all duration-200 hover:bg-white/20"
            >
              Continue in Browser
            </button>

            {onGetApp && (
              <button
                onClick={onGetApp}
                className="w-full rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-6 py-3 font-medium text-cyan-100 transition-all duration-200 hover:bg-cyan-500/20"
              >
                Get the App
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SharedAlbumPage: React.FC = () => {
  const { albumId, ownerId } = useParams<{ albumId: string; ownerId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const { user, isLoading: userLoading, fetchUser } = useUser();
  const { createShare } = useAlbumShare(user?.id);
  const { refreshAuth } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasProcessed, setHasProcessed] = useState(false);
  const isProcessingRef = useRef(false);

  const mobilePlatform = useMemo(() => getMobilePlatform(), []);

  const browserShareUrl = useMemo(() => {
    if (!token || !albumId || !ownerId) {
      return "";
    }

    return buildBrowserShareUrl({
      baseUrl: window.location.origin,
      ownerId,
      albumId,
      token,
      openInBrowser: true,
    });
  }, [albumId, ownerId, token]);

  const nativeShareUrl = useMemo(() => {
    if (!token || !albumId || !ownerId || mobilePlatform === "desktop") {
      return "";
    }

    return buildMobileAppShareUrl({
      platform: mobilePlatform,
      ownerId,
      albumId,
      token,
      webFallbackUrl: browserShareUrl,
    });
  }, [albumId, browserShareUrl, mobilePlatform, ownerId, token]);

  const storeUrl = useMemo(() => getStoreUrl(mobilePlatform), [mobilePlatform]);

  const shouldShowMobileDecision =
    isNativeAppShareHandoffEnabled &&
    Boolean(token && albumId && ownerId) &&
    mobilePlatform !== "desktop" &&
    searchParams.get("openInBrowser") !== "1";

  useEffect(() => {
    const handleSharedAlbumAccess = async () => {
      if (shouldShowMobileDecision) {
        return;
      }

      if (!token) {
        setError("No token provided in the URL");
        setIsLoading(false);
        return;
      }

      if (userLoading || hasProcessed || isProcessingRef.current) {
        return;
      }

      try {
        isProcessingRef.current = true;
        setHasProcessed(true);
        setIsLoading(true);
        setError(null);

        const isLoggedIn = await Session.doesSessionExist();

        if (isLoggedIn && user) {
          authService.clearAnonymousAuth();
          await createShare(token);
          await Session.attemptRefreshingSession();

          if (!albumId || !ownerId) {
            throw new Error("Missing albumId or ownerId in URL");
          }

          navigate(`/owners/${ownerId}/albums/${albumId}`, { replace: true });
          return;
        }

        const existingAnonymousToken = authService.getAnonymousToken();

        if (existingAnonymousToken) {
          await createShare(token);
          await refreshAuth();

          if (!albumId || !ownerId) {
            throw new Error("Missing albumId or ownerId in URL");
          }

          navigate(`/owners/${ownerId}/albums/${albumId}`, { replace: true });
          return;
        }

        await authService.signInWithLink(token);
        await refreshAuth();
        fetchUser();

        if (!albumId || !ownerId) {
          throw new Error("Missing albumId or ownerId in URL");
        }

        navigate(`/owners/${ownerId}/albums/${albumId}`, { replace: true });
      } catch (err) {
        isProcessingRef.current = false;
        setHasProcessed(false);
        console.error("Error handling shared album access:", err);
        setError(err instanceof Error ? err.message : "Failed to access shared album");
        setIsLoading(false);
      }
    };

    handleSharedAlbumAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    albumId,
    createShare,
    fetchUser,
    hasProcessed,
    navigate,
    ownerId,
    refreshAuth,
    shouldShowMobileDecision,
    token,
    user,
    userLoading,
  ]);

  if (shouldShowMobileDecision) {
    return (
      <MobileShareDecision
        onOpenApp={() => {
          window.location.href = nativeShareUrl;
        }}
        onContinueInBrowser={() => {
          window.location.href = browserShareUrl;
        }}
        onGetApp={
          storeUrl
            ? () => {
                window.location.href = storeUrl;
              }
            : null
        }
      />
    );
  }

  if (isLoading) {
    return (
      <div
        data-section="shared-album-page-root"
        className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900"
      >
        <LoadingSpinner message="Loading shared album..." size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-section="shared-album-page-root"
        className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900"
      >
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 h-80 w-80 animate-blob rounded-full bg-purple-500 opacity-20 mix-blend-multiply blur-xl filter"></div>
          <div className="animation-delay-2000 absolute -bottom-40 -left-40 h-80 w-80 animate-blob rounded-full bg-blue-500 opacity-20 mix-blend-multiply blur-xl filter"></div>
          <div className="animation-delay-4000 absolute top-40 left-1/2 h-80 w-80 animate-blob rounded-full bg-pink-500 opacity-20 mix-blend-multiply blur-xl filter"></div>
        </div>

        <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-4">
          <div className="w-full max-w-md rounded-3xl border border-white/20 bg-white/10 p-8 text-center shadow-2xl backdrop-blur-xl">
            <div className="mb-6">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-pink-500">
                <svg className="h-10 w-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>

            <h1 className="mb-3 text-3xl font-bold text-white">Error Loading Album</h1>

            <p className="mb-6 text-gray-300">{error}</p>

            <button
              onClick={() => navigate("/")}
              className="w-full rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-3 font-medium text-white shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:from-purple-500 hover:to-blue-500 hover:shadow-purple-500/25"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SharedAlbumPage;
